// A message's time in the format chosen in Settings → Appearance (#52). The long form sits beside the name; the short
// form fits the narrow gutter beside grouped messages (and compact display's first line).
import type { ReactNode } from "react";
import { exactAge, exactAgeShort, relativeAge, relativeAgeShort, type TimestampFormat } from "@bloom/shared";
import { useNow, useTimestampFormat } from "../timestampFormat.ts";

export const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });
/** "3:04", without AM/PM, for the narrow gutter. */
export const shortTime = (ts: number) =>
  timeFmt.formatToParts(ts).filter((p) => p.type === "hour" || p.type === "minute" || p.type === "literal").map((p) => p.value).join("").trim();
/** The tooltip on a time shown as how long ago: "Saturday, October 24, 2026 at 2:05:33 PM". */
const fullFmt = new Intl.DateTimeFormat(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" });

export function formatMessageTime(ts: number, now: number, format: TimestampFormat, short: boolean): string {
  switch (format) {
    case "relative":
      return short ? relativeAgeShort(now - ts) : relativeAge(now - ts);
    case "exact":
      return short ? exactAgeShort(now - ts) : exactAge(now - ts);
    default:
      return short ? shortTime(ts) : timeFmt.format(ts);
  }
}

/** Hovering a time shows its other form (#122): how long ago for the time of day, the full date for how long ago. */
export function messageTimeTitle(ts: number, now: number, format: TimestampFormat): string {
  return format === "default" ? relativeAge(now - ts) : fullFmt.format(ts);
}

/** How often a time in this format needs drawing again: seconds while a relative time counts them, else minutes (the
 *  default's tooltip says how long ago). */
function tickFor(format: TimestampFormat, ts: number): number {
  return format === "relative" && Date.now() - ts < 2 * 60_000 ? 1000 : 60_000;
}

export function MessageTime({ ts, short = false, className }: { ts: number; short?: boolean; className?: string }): ReactNode {
  const format = useTimestampFormat();
  const now = useNow(tickFor(format, ts));
  return (
    <time className={className} dateTime={new Date(ts).toISOString()} title={messageTimeTitle(ts, now, format)}>
      {formatMessageTime(ts, now, format, short)}
    </time>
  );
}
